import React from 'react';
import { cn } from '@h2/ui';
import Icon from '../ui/icon';
import { useNotificationStore, Notification, NotificationType } from '../../stores/useNotificationStore';

const NotificationPanel: React.FC = () => {
  const {
    isNotificationOpen,
    notifications,
    closeNotification,
    markAsRead,
    markAllAsRead,
    removeNotification
  } = useNotificationStore();

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'danger':
        return 'AlertTriangle';
      case 'warning':
        return 'AlertCircle';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case 'danger':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          icon: 'text-red-600 dark:text-red-400',
          title: 'text-red-900 dark:text-red-100',
          text: 'text-red-700 dark:text-red-300'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          icon: 'text-yellow-600 dark:text-yellow-400',
          title: 'text-yellow-900 dark:text-yellow-100',
          text: 'text-yellow-700 dark:text-yellow-300'
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          icon: 'text-blue-600 dark:text-blue-400',
          title: 'text-blue-900 dark:text-blue-100',
          text: 'text-blue-700 dark:text-blue-300'
        };
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      // 여기서 라우팅 처리 (현재는 콘솔 로그)
      console.log(`Navigate to: ${notification.actionUrl}`);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (!isNotificationOpen) return null;

  return (
    <div className="fixed top-16 right-4 w-96 max-h-[600px] bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden z-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center space-x-2">
          <Icon name="Bell" size={20} className="text-gray-700 dark:text-gray-300" />
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">알림</h3>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
              title="모두 읽음 처리"
            >
              모두 읽음
            </button>
          )}
          <button
            onClick={closeNotification}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
            title="알림 닫기"
          >
            <Icon name="X" size={16} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Icon name="Bell" size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
            <p className="text-lg font-medium mb-2">알림이 없습니다</p>
            <p className="text-sm">새로운 알림이 있으면 여기에 표시됩니다.</p>
          </div>
        ) : (
          <div className="p-2">
            {notifications.map((notification) => {
              const colors = getNotificationColor(notification.type);
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    'relative p-4 mb-2 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md',
                    colors.bg,
                    colors.border,
                    !notification.isRead && 'ring-2 ring-blue-100'
                  )}
                >
                  <div className="flex items-start space-x-3">
                    <div className={cn('flex-shrink-0 mt-0.5')}>
                      <Icon 
                        name={getNotificationIcon(notification.type)} 
                        size={18} 
                        className={colors.icon} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className={cn('font-medium text-sm', colors.title)}>
                          {notification.title}
                        </h4>
                        <div className="flex items-center space-x-2 ml-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {formatRelativeTime(notification.timestamp)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="p-1 hover:bg-white hover:bg-opacity-50 dark:hover:bg-gray-700 dark:hover:bg-opacity-50 rounded transition-colors"
                            title="알림 삭제"
                          >
                            <Icon name="X" size={12} className="text-gray-400 dark:text-gray-500" />
                          </button>
                        </div>
                      </div>
                      <p className={cn('text-sm mt-1', colors.text)}>
                        {notification.message}
                      </p>
                      {notification.actionUrl && (
                        <div className="mt-2">
                          <span className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                            자세히 보기 →
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!notification.isRead && (
                    <div className="absolute top-2 right-2 w-2 h-2 bg-blue-600 rounded-full"></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
